import { useEffect, useRef, useState } from "react";
import { CheckCircle, ArrowRight } from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "./DataTable";
import { StatCard } from "./StatCard";
import { IssueItem } from "./IssueItem";
import { getDataProgress } from "../../api";
import { ProgressBar } from "../ProgressBar";
import { logEvent } from "@shared/analytics/analytics";
import {
  useValidationErrors,
  useApplyFormatFixes,
  useFinalizeStage4,
  useValidationSummary,
} from "../../hooks";
import { transformExamplesToTable } from "./transformExamples";
import { toast } from "@shared/hooks/use-toast";
import { PageHeader } from "@shared/components/PageHeader";

const DataValidation = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [isFinalizingStage4, setIsFinalizingStage4] = useState(false);
  const [stage4Progress, setStage4Progress] = useState(0);

  const finalizeStage4Mutation = useFinalizeStage4();
  const { data: validationSummary, refetch: summaryRefetch } =
    useValidationSummary(sessionId);

  // Poll progress when finalizing
  useEffect(() => {
    if (!isFinalizingStage4 || !sessionId) {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        const progress = await getDataProgress(sessionId);

        setStage4Progress(progress.percentage);

        if (progress.status === "completed" && progress.percentage >= 100) {
          clearInterval(pollInterval);
          setIsFinalizingStage4(false);

          navigate(
            `/onboarding/data-migration/migration-execution/${sessionId}`
          );

          logEvent("Data Upload Approved");
        } else if (progress.status === "failed") {
          toast({
            title: `Failed to finalize. Please try again`,
            description: ``,
          });
          clearInterval(pollInterval);
          setIsFinalizingStage4(false);
        }
      } catch (error) {
        toast({
          title: `Error polling State 4 progress, ${error}`,
          description: ``,
        });
      }
    }, 2000);

    return () => {
      clearInterval(pollInterval);
    };
  }, [isFinalizingStage4, sessionId]);

  // Fetch validation errors from API
  const {
    data: validationData,
    isLoading: isLoadingErrors,
    refetch: refetchErrors,
  } = useValidationErrors(sessionId);

  const applyFixesMutation = useApplyFormatFixes();

  const [isFixing, setIsFixing] = useState(false);
  const [fixTaskId, setFixTaskId] = useState<string | null>(null);

  const [stats, setStats] = useState({
    totalRecords: 0,
    validRecords: 0,
    errors: 0,
    validPercentage: 0,
    warningPercentage: 0,
    errorPercentage: 0,
  });

  // Calculate stats from API response
  const hasLoggedRef = useRef(false);

  useEffect(() => {
    if (validationSummary) {
      const totalErrors = validationSummary?.recordsWithErrors;
      const total = validationSummary?.totalRecords || 0;
      const valid = total;
      const safeTotal = total > 0 ? total : 1;

      setStats({
        totalRecords: total,
        validRecords: valid,
        errors: totalErrors,
        validPercentage: Number(((valid / safeTotal) * 100).toFixed(1)),
        warningPercentage: 0,
        errorPercentage: Number(((totalErrors / safeTotal) * 100).toFixed(1)),
      });

      // Only log once
      if (!hasLoggedRef.current) {
        logEvent("Data Upload Field Mapping Completion", {
          validRecords: valid,
        });

        if (totalErrors > 0) {
          logEvent("Data Upload Validation Errors", {
            errorCount: totalErrors,
          });
        }
        hasLoggedRef.current = true;
      }
    }
  }, [validationSummary]);
  // Poll task progress after applying fixes
  useEffect(() => {
    if (!fixTaskId || !sessionId) return;

    const pollInterval = setInterval(async () => {
      try {
        const progress = await getDataProgress(sessionId);

        if (progress.status === "completed") {
          clearInterval(pollInterval);
          setFixTaskId(null);
          setIsFixing(false);
          await refetchErrors();
          await summaryRefetch();
          toast({
            title: `All issues have been successfully resolved!`,
            description: ``,
          });
        } else if (progress.status === "failed") {
          clearInterval(pollInterval);
          setFixTaskId(null);
          setIsFixing(false);
          toast({
            title: `Failed to fix issues please try again`,
            description: ``,
          });
        }
      } catch (error) {
        toast({
          title: `Error polling progress:", ${error}`,
          description: ``,
        });
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [fixTaskId, sessionId]);

  const handleFixAll = async () => {
    if (!sessionId) {
      toast({
        title: `No session ID available`,
        description: ``,
      });
      return;
    }

    setIsFixing(true);

    try {
      const result = await applyFixesMutation.mutateAsync(sessionId);
      setFixTaskId(result.taskId);
    } catch (err) {
      toast({
        title: `Failed to fix all issues. Please try again:", ${err}`,
        description: ``,
      });

      setIsFixing(false);
    }
  };

  const handleApproveAndProceed = async () => {
    if (!sessionId) {
      toast({
        title: `No session ID available`,
        description: ``,
      });
      return;
    }

    setIsFinalizingStage4(true);

    try {
      const result = await finalizeStage4Mutation.mutateAsync({
        sessionId,
        confirmed: true,
        proceedWithErrors: true,
      });

      // Note: We receive a taskId but don't need to store it
      // The polling useEffect will trigger automatically when isFinalizingStage4 becomes true
    } catch (error) {
      toast({
        title: `Failed to finalize stage 4:", ${error}`,
        description: ``,
      });
      setIsFinalizingStage4(true);
    }
  };

  return (
    <div className="min-h-screen w-full p-6">
      <ProgressBar
        currentStep={5}
        totalSteps={7}
        progress={70}
        currentStepName={"Data Validations"}
      />
      <div className="mx-auto space-y-8 mt-4">
        <div className="flex flex-col">
          <PageHeader
            title="Validation Results"
            subtitle="Review issues and approve data for migration."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard
            title="Total Records"
            value={stats.totalRecords}
            color="gray"
          />
          <StatCard
            title="Valid Records"
            value={stats.validRecords}
            subtitle={`${stats.validPercentage}%`}
            color="green"
          />

          <StatCard
            title="Errors"
            value={stats.errors}
            subtitle={`${stats.errorPercentage}%`}
            color="red"
          />
        </div>

        <div className="validation-container">
          <div className="mb-6">
            <button
              onClick={handleFixAll}
              disabled={isFixing || !validationData?.result?.length}
              className={`px-4 py-2 rounded float-right transition-colors ${
                isFixing || !validationData?.result?.length
                  ? "bg-gray-400 cursor-not-allowed text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
            >
              {isFixing ? "Fixing..." : "Fix ALL"}
            </button>
            <div className="clear-both"></div>
          </div>

          <div className="divide-y divide-gray-200">
            {isLoadingErrors ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Loading validation errors...</p>
              </div>
            ) : validationData?.result?.length > 0 ? (
              validationData.result.map((error, index) => (
                <div className="px-6" key={index}>
                  <IssueItem
                    sessionId={sessionId}
                    error={error}
                    type={
                      error.error_type?.toLowerCase().includes("incorrect")
                        ? "error"
                        : "warning"
                    }
                    title={`${error.field} - ${error.error_type}`}
                    description={error.message || ""}
                    issue={{
                      errorType: error.error_type,
                      isStandardizeFormat: error.can_auto_fix,
                      isNumFormatConfigured: false,
                      isConfigureGenerate: false,
                    }}
                    uploadId={sessionId}
                    utilityId={sessionId}
                    tableComponent={
                      error.examples?.length > 0 ? (
                        <DataTable
                          {...transformExamplesToTable(error.examples)}
                        />
                      ) : null
                    }
                  />
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  ✓ All data validation issues have been resolved
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end mt-8">
          <button
            onClick={handleApproveAndProceed}
            disabled={isFinalizingStage4}
            className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-200 ${
              isFinalizingStage4
                ? "bg-gray-400 cursor-not-allowed text-white"
                : "bg-blue-600 text-white hover:bg-blue-700 hover:shadow-lg hover:-translate-y-0.5"
            }`}
          >
            <CheckCircle size={18} />
            <span>
              {isFinalizingStage4
                ? `Processing... ${stage4Progress}%`
                : "Approve & Proceed to Migration"}
            </span>
            {!isFinalizingStage4 && <ArrowRight size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DataValidation;
